import { useSupabase } from '@packages/supabase'
import { useCallback } from 'react'
import useSWR from 'swr'
import type {
  BillingModel,
  IcpBillingProfile,
  IcpClaimsRefreshResult,
  PlanCapMatrixRow,
  PlanFeatureMatrixRow,
  StripeCatalogPlan,
  TenantBillingBaselineResult,
  TenantBillingDisconnectResult
} from './types'

async function stripeAdminFetch(
  supabase: ReturnType<typeof useSupabase>['supabase'],
  path: string,
  body: Record<string, unknown>
) {
  const { data, error } = await supabase.functions.invoke(`stripe/${path}`, {
    method: 'POST',
    body
  })
  if (error) throw error
  if (data?.error) throw new Error(String(data.error))
  return data
}

export function useIcpBillingProfiles() {
  const { supabase } = useSupabase()

  return useSWR<IcpBillingProfile[]>('admin-icp-billing-profiles', async () => {
    const { data, error } = await supabase.rpc('admin_list_icp_billing_profiles')
    if (error) throw error
    return (data ?? []) as IcpBillingProfile[]
  })
}

export function usePlanFeatureMatrix() {
  const { supabase } = useSupabase()

  return useSWR<PlanFeatureMatrixRow[]>(
    'admin-plan-feature-matrix',
    async () => {
      const { data, error } = await supabase.rpc('admin_fetch_plan_feature_matrix')
      if (error) throw error
      return (data ?? []) as PlanFeatureMatrixRow[]
    }
  )
}

export function usePlanCapMatrix() {
  const { supabase } = useSupabase()

  return useSWR<PlanCapMatrixRow[]>('admin-plan-cap-matrix', async () => {
    const { data, error } = await supabase.rpc('admin_fetch_plan_cap_matrix')
    if (error) throw error
    return (data ?? []) as PlanCapMatrixRow[]
  })
}

export function useStripeCatalog(tenantType: string, currency = 'eur') {
  const { supabase } = useSupabase()

  return useSWR<StripeCatalogPlan[]>(
    ['admin-stripe-catalog', tenantType, currency],
    async () => {
      const { data, error } = await supabase.rpc('stripe_get_billing_plans', {
        p_tenant_type: tenantType,
        p_currency: currency,
        p_interval: 'month'
      })
      if (error) throw error
      return (data ?? []) as StripeCatalogPlan[]
    }
  )
}

export function useStripeAdminActions() {
  const { supabase } = useSupabase()

  const upsertIcpProfile = useCallback(
    async (args: {
      tenantType: string
      billingModel: BillingModel
      enabled: boolean
      description?: string | null
    }) => {
      const { error } = await supabase.rpc('admin_upsert_icp_billing_profile', {
        p_tenant_type: args.tenantType,
        p_billing_model: args.billingModel,
        p_enabled: args.enabled,
        p_description: args.description ?? null
      })
      if (error) throw error
    },
    [supabase]
  )

  const setPlanFeatures = useCallback(
    async (tenantType: string, planTier: string, featureKeys: string[]) => {
      const { data, error } = await supabase.rpc('admin_set_plan_features', {
        p_tenant_type: tenantType,
        p_plan_tier: planTier,
        p_feature_keys: featureKeys
      })
      if (error) throw error
      return data as {
        tenants_refreshed?: number
        users_refreshed?: number
      } | null
    },
    [supabase]
  )

  const setPlanCaps = useCallback(
    async (
      tenantType: string,
      planTier: string,
      caps: Record<string, number | null>
    ) => {
      const p_caps: Record<string, number | null> = {}
      for (const [key, val] of Object.entries(caps)) {
        p_caps[key] = val
      }
      const { error } = await supabase.rpc('admin_set_plan_caps', {
        p_tenant_type: tenantType,
        p_plan_tier: planTier,
        p_caps
      })
      if (error) throw error
    },
    [supabase]
  )

  const setTenantBillingPlan = useCallback(
    async (
      tenantId: string,
      planTier: string,
      options?: { swapStripe?: boolean; currency?: string }
    ) => {
      return stripeAdminFetch(supabase, 'admin/set-tenant-plan', {
        tenantId,
        planTier,
        swapStripe: options?.swapStripe ?? true,
        currency: options?.currency ?? 'eur'
      }) as Promise<{ billing_plan: string; stripeSwapped: boolean }>
    },
    [supabase]
  )

  const disconnectTenantBilling = useCallback(
    async (
      tenantId: string,
      options?: { clearCustomer?: boolean; cancelStripe?: boolean }
    ) => {
      return stripeAdminFetch(supabase, 'admin/disconnect-tenant', {
        tenantId,
        clearCustomer: options?.clearCustomer ?? false,
        cancelStripe: options?.cancelStripe ?? true
      }) as Promise<TenantBillingDisconnectResult & { ok: boolean }>
    },
    [supabase]
  )

  const baselineTenantPlanFeatures = useCallback(
    async (
      tenantId: string,
      options?: { planTier?: string | null; clearOverrides?: boolean }
    ) => {
      const { data, error } = await supabase.rpc(
        'admin_baseline_tenant_plan_features',
        {
          p_tenant_id: tenantId,
          p_plan_tier: options?.planTier ?? null,
          p_clear_overrides: options?.clearOverrides ?? true
        }
      )
      if (error) throw error
      return data as TenantBillingBaselineResult
    },
    [supabase]
  )

  const refreshIcpTenantClaims = useCallback(
    async (tenantType: string, planTier?: string | null) => {
      const { data, error } = await supabase.rpc('admin_refresh_icp_tenant_claims', {
        p_tenant_type: tenantType,
        p_plan_tier: planTier ?? null
      })
      if (error) throw error
      return data as IcpClaimsRefreshResult
    },
    [supabase]
  )

  return {
    upsertIcpProfile,
    setPlanFeatures,
    setPlanCaps,
    setTenantBillingPlan,
    disconnectTenantBilling,
    baselineTenantPlanFeatures,
    refreshIcpTenantClaims
  }
}
